/**
 * CR306: Draft-Aware V2 Spec Resolver
 * 
 * Merges variant + template_json + overrides_json + draftOverrides
 * for instant preview updates before Save.
 * 
 * Canonical layering (high precedence → low):
 * 1. draftOverrides (preview-only, not persisted)
 * 2. overrides_json (persisted V2 overrides)
 * 3. template_json (deal-type baseline)
 * 4. variant template_json (base structure)
 * 
 * V2 ignores legacy_template_json entirely.
 */

import type {
  DealCardSpecV2,
  DealCardOverridesV2,
  ResolvedDealCardSpecV2,
  SurfaceOverrides,
  DealCardSurface,
  DealCardBlockSpec,
} from "./types";
import {
  resolveDealCardSpecV2,
} from "./resolveSpec";

// ================================================================
// DRAFT OVERRIDES TYPE (Editor → Preview)
// ================================================================

/**
 * Draft overrides structure that matches editor state.
 * These are NOT persisted until Save.
 */
export interface V2DraftOverrides {
  /** styleOverrides keys (showMerchantLogo, showRibbon, etc.) */
  styleOverrides?: Record<string, unknown>;
  /** Label overrides */
  labels?: Record<string, string>;
  /** Header composition overrides */
  header?: {
    format?: string;
    show_reward_brand?: boolean;
    reward_brand_label?: string;
    show_promo_merchant?: boolean;
    promo_merchant_label?: string;
    show_reward_type_chip?: boolean;
  };
  /** Surface block toggles */
  surfaceOverrides?: {
    primary?: SurfaceOverrides;
    details?: SurfaceOverrides;
  };
}

// ================================================================
// DRAFT-FIRST RESOLVER
// ================================================================

/**
 * Resolve V2 spec with draft overrides applied on top.
 * 
 * Use this in the Template Editor preview for instant updates.
 * 
 * @param variantSpec - Base variant (from DB or DEFAULT_SPEC_V2)
 * @param overrides - Persisted overrides (from deal_card_templates.overrides_json)
 * @param dealTypeKey - The deal type
 * @param variantKey - The variant key
 * @param sources - Source tracking
 * @param draftOverrides - Unsaved editor changes (highest precedence)
 * @param legacyStyling - Legacy styling for migration shim
 */
export function resolveSpecV2WithDraft(
  variantSpec: DealCardSpecV2 | null,
  overrides: DealCardOverridesV2 | null,
  dealTypeKey: string,
  variantKey: string,
  sources: { variant: "db" | "fallback"; overrides: "db" | "fallback" },
  draftOverrides?: V2DraftOverrides | null,
  legacyStyling?: Record<string, unknown> | null
): ResolvedDealCardSpecV2 {
  // First, resolve the base spec without drafts
  const baseResolved = resolveDealCardSpecV2(
    variantSpec,
    overrides,
    dealTypeKey,
    variantKey,
    sources,
    legacyStyling
  );
  
  // If no draft overrides, return base resolution
  if (!draftOverrides) {
    return baseResolved;
  }
  
  // Apply draft overrides on top (highest precedence)
  const mergedStyleOverrides = {
    ...baseResolved.resolved.styleOverrides,
    ...draftOverrides.styleOverrides,
  };
  
  const mergedLabels = {
    ...baseResolved.resolved.labels,
    ...draftOverrides.labels,
  };
  
  // Apply surface overrides if present (CR432: also apply blockOrder for drag/drop preview)
  let primarySurface = baseResolved.surfaces.primary;
  let detailsSurface = baseResolved.surfaces.details;
  
  if (draftOverrides.surfaceOverrides?.primary?.blockToggles) {
    primarySurface = applyDraftBlockToggles(
      primarySurface,
      draftOverrides.surfaceOverrides.primary.blockToggles
    );
  }
  if (draftOverrides.surfaceOverrides?.primary?.blockOrder?.length) {
    primarySurface = applyDraftBlockOrder(
      primarySurface,
      draftOverrides.surfaceOverrides.primary.blockOrder
    );
  }
  
  if (draftOverrides.surfaceOverrides?.details?.blockToggles) {
    detailsSurface = applyDraftBlockToggles(
      detailsSurface,
      draftOverrides.surfaceOverrides.details.blockToggles
    );
  }
  if (draftOverrides.surfaceOverrides?.details?.blockOrder?.length) {
    detailsSurface = applyDraftBlockOrder(
      detailsSurface,
      draftOverrides.surfaceOverrides.details.blockOrder
    );
  }
  
  return {
    ...baseResolved,
    surfaces: {
      primary: primarySurface,
      details: detailsSurface,
    },
    resolved: {
      ...baseResolved.resolved,
      styleOverrides: mergedStyleOverrides,
      labels: mergedLabels,
      // Add draft source tracking for debug panel
      sources: {
        ...baseResolved.resolved.sources,
        // We could add "draft" here but keeping simple for now
      },
    },
  };
}

/**
 * Apply draft block toggles to a surface.
 */
function applyDraftBlockToggles(
  surface: DealCardSurface,
  toggles: Record<string, boolean>
): DealCardSurface {
  return {
    blocks: surface.blocks.map((block): DealCardBlockSpec => {
      const toggle = toggles[block.id];
      if (toggle === undefined) return block;
      return { ...block, enabled: toggle };
    }),
  };
}

/**
 * CR432: Apply draft block order to a surface.
 * Reorders blocks according to the provided order array.
 * Blocks not in the order array are appended at the end.
 */
function applyDraftBlockOrder(
  surface: DealCardSurface,
  blockOrder: string[]
): DealCardSurface {
  const orderMap = new Map<string, number>();
  blockOrder.forEach((id, index) => orderMap.set(id, index));
  
  const orderedBlocks: DealCardBlockSpec[] = [];
  const unorderedBlocks: DealCardBlockSpec[] = [];
  
  for (const block of surface.blocks) {
    if (orderMap.has(block.id)) {
      orderedBlocks.push(block);
    } else {
      unorderedBlocks.push(block);
    }
  }
  
  orderedBlocks.sort((a, b) => {
    const orderA = orderMap.get(a.id) ?? Infinity;
    const orderB = orderMap.get(b.id) ?? Infinity;
    return orderA - orderB;
  });
  
  const combined = [...orderedBlocks, ...unorderedBlocks];
  return {
    blocks: combined.map((block, index) => ({ ...block, order: index })),
  };
}

// ================================================================
// EDITOR → V2 OVERRIDES BUILDER
// ================================================================

/**
 * Build V2 canonical overrides_json from editor draft state.
 * 
 * This is used by the save pipeline to write to deal_card_templates.overrides_json.
 * 
 * CRITICAL: Must use `typeof v === 'boolean'` checks, NOT `if (v)` truthy checks,
 * to ensure false values are persisted correctly.
 */
export function buildV2OverridesJson(
  variantKey: string,
  editorState: {
    styling?: Record<string, unknown>;
    labels?: Record<string, string>;
    header?: Record<string, unknown>;
    sections?: Record<string, boolean>;
    extended_sections?: Record<string, boolean>;
  },
  existingOverrides?: Record<string, unknown>
): DealCardOverridesV2 {
  const overrides: DealCardOverridesV2 = {
    schemaVersion: 2,
    variantKey,
    labels: {},
    styleOverrides: {},
    surfaceOverrides: {
      primary: { blockToggles: {} },
      details: { blockToggles: {} },
    },
  };
  
  // Merge existing overrides first
  if (existingOverrides) {
    if (existingOverrides.labels) {
      overrides.labels = { ...existingOverrides.labels as Record<string, string> };
    }
    if (existingOverrides.styleOverrides) {
      overrides.styleOverrides = { ...existingOverrides.styleOverrides as Record<string, unknown> };
    }
    if (existingOverrides.surfaceOverrides) {
      const so = existingOverrides.surfaceOverrides as Record<string, SurfaceOverrides>;
      if (so.primary?.blockToggles) {
        overrides.surfaceOverrides!.primary!.blockToggles = { ...so.primary.blockToggles };
      }
      if (so.details?.blockToggles) {
        overrides.surfaceOverrides!.details!.blockToggles = { ...so.details.blockToggles };
      }
    }
    if (existingOverrides.header) {
      (overrides as any).header = { ...existingOverrides.header as Record<string, unknown> };
    }
  }
  
  // Apply new labels
  if (editorState.labels) {
    overrides.labels = { ...overrides.labels, ...editorState.labels };
  }
  
  // Apply header config
  if (editorState.header) {
    (overrides as any).header = { ...(overrides as any).header, ...editorState.header };
  }
  
  // Apply styleOverrides - CRITICAL: check typeof === 'boolean' for false values
  if (editorState.styling) {
    const s = editorState.styling;
    
    // Visibility toggles (boolean)
    if (typeof s.showMerchantLogo === 'boolean') {
      overrides.styleOverrides!.showMerchantLogo = s.showMerchantLogo;
    }
    if (typeof s.showMerchantName === 'boolean') {
      overrides.styleOverrides!.showMerchantName = s.showMerchantName;
    }
    if (typeof s.showRibbon === 'boolean') {
      overrides.styleOverrides!.showRibbon = s.showRibbon;
    }
    if (typeof s.showStarburst === 'boolean') {
      overrides.styleOverrides!.showStarburst = s.showStarburst;
    }
    
    // Card styling
    if (s.backgroundType !== undefined) overrides.styleOverrides!.backgroundType = s.backgroundType;
    if (s.backgroundColor !== undefined) overrides.styleOverrides!.backgroundColor = s.backgroundColor;
    if (s.gradientFrom !== undefined) overrides.styleOverrides!.gradientFrom = s.gradientFrom;
    if (s.gradientTo !== undefined) overrides.styleOverrides!.gradientTo = s.gradientTo;
    if (s.gradientDirection !== undefined) overrides.styleOverrides!.gradientDirection = s.gradientDirection;
    if (typeof s.borderWidth === 'number') overrides.styleOverrides!.borderWidth = s.borderWidth;
    if (s.borderColor !== undefined) overrides.styleOverrides!.borderColor = s.borderColor;
    if (s.cornerRadiusPreset !== undefined) overrides.styleOverrides!.cornerRadiusPreset = s.cornerRadiusPreset;
    if (typeof s.cornerRadius === 'number') overrides.styleOverrides!.cornerRadius = s.cornerRadius;
    if (s.shadowIntensity !== undefined) overrides.styleOverrides!.shadowIntensity = s.shadowIntensity;
    
    // Header styling
    if (s.headerHeight !== undefined) overrides.styleOverrides!.headerHeight = s.headerHeight;
    if (s.headerBackgroundType !== undefined) overrides.styleOverrides!.headerBackgroundType = s.headerBackgroundType;
    if (s.headerBackgroundColor !== undefined) overrides.styleOverrides!.headerBackgroundColor = s.headerBackgroundColor;
    if (s.headerGradientFrom !== undefined) overrides.styleOverrides!.headerGradientFrom = s.headerGradientFrom;
    if (s.headerGradientTo !== undefined) overrides.styleOverrides!.headerGradientTo = s.headerGradientTo;
    
    // Decorations
    if (s.ribbonPosition !== undefined) overrides.styleOverrides!.ribbonPosition = s.ribbonPosition;
    if (typeof s.ribbonOffsetX === 'number') overrides.styleOverrides!.ribbonOffsetX = s.ribbonOffsetX;
    if (typeof s.ribbonOffsetY === 'number') overrides.styleOverrides!.ribbonOffsetY = s.ribbonOffsetY;
    if (s.starburstPosition !== undefined) overrides.styleOverrides!.starburstPosition = s.starburstPosition;
    if (typeof s.starburstOffsetX === 'number') overrides.styleOverrides!.starburstOffsetX = s.starburstOffsetX;
    if (typeof s.starburstOffsetY === 'number') overrides.styleOverrides!.starburstOffsetY = s.starburstOffsetY;
  }
  
  // Apply block toggles from sections
  const allSections = { ...editorState.sections, ...editorState.extended_sections };
  
  // Map V1 section keys to V2 block IDs
  const primaryBlockMap: Record<string, string> = {
    headline: 'headline',
    promo_badges: 'promo_badges',
    details_pills: 'details_pills',
    dates: 'dates',
    effective_value_box: 'value_box',
    qualifying_spend_box: 'spend_box',
    reward_box: 'reward_box',
    eligibility_box: 'eligibility_box',
    source_buttons: 'cta_button',
    ribbon: 'ribbon',
  };
  
  const detailsBlockMap: Record<string, string> = {
    terms: 'terms',
    fine_print: 'fine_print',
    source: 'source',
    evidence: 'evidence',
    actions: 'actions',
  };
  
  for (const [sectionKey, enabled] of Object.entries(allSections)) {
    if (typeof enabled !== 'boolean') continue;
    
    const primaryBlockId = primaryBlockMap[sectionKey];
    if (primaryBlockId) {
      overrides.surfaceOverrides!.primary!.blockToggles![primaryBlockId] = enabled;
    }
    
    const detailsBlockId = detailsBlockMap[sectionKey];
    if (detailsBlockId) {
      overrides.surfaceOverrides!.details!.blockToggles![detailsBlockId] = enabled;
    }
  }
  
  return overrides;
}

// ================================================================
// DRAFT → PREVIEW BUILDER
// ================================================================

/**
 * Convert editor draft state to V2DraftOverrides for preview.
 * 
 * This is used to build draftOverrides from the live editor state
 * for instant preview updates.
 */
export function buildDraftOverridesFromEditor(
  editorState: {
    styling?: Record<string, unknown>;
    labels?: Record<string, string>;
    header?: Record<string, unknown>;
    sections?: Record<string, boolean>;
    extended_sections?: Record<string, boolean>;
  }
): V2DraftOverrides {
  const draftOverrides: V2DraftOverrides = {
    styleOverrides: {},
    labels: {},
    surfaceOverrides: {
      primary: { blockToggles: {} },
      details: { blockToggles: {} },
    },
  };
  
  // Apply styleOverrides
  if (editorState.styling) {
    const s = editorState.styling;
    if (typeof s.showMerchantLogo === 'boolean') {
      draftOverrides.styleOverrides!.showMerchantLogo = s.showMerchantLogo;
    }
    if (typeof s.showMerchantName === 'boolean') {
      draftOverrides.styleOverrides!.showMerchantName = s.showMerchantName;
    }
    if (typeof s.showRibbon === 'boolean') {
      draftOverrides.styleOverrides!.showRibbon = s.showRibbon;
    }
    if (typeof s.showStarburst === 'boolean') {
      draftOverrides.styleOverrides!.showStarburst = s.showStarburst;
    }
    // Copy other styling keys
    for (const [key, value] of Object.entries(s)) {
      if (value !== undefined && draftOverrides.styleOverrides![key] === undefined) {
        draftOverrides.styleOverrides![key] = value;
      }
    }
  }
  
  // Apply labels
  if (editorState.labels) {
    draftOverrides.labels = { ...editorState.labels };
  }
  
  // Apply header
  if (editorState.header) {
    draftOverrides.header = editorState.header as V2DraftOverrides['header'];
  }
  
  // Apply block toggles
  const allSections = { ...editorState.sections, ...editorState.extended_sections };
  
  const primaryBlockMap: Record<string, string> = {
    headline: 'headline',
    promo_badges: 'promo_badges',
    details_pills: 'details_pills',
    dates: 'dates',
    effective_value_box: 'value_box',
    qualifying_spend_box: 'spend_box',
    reward_box: 'reward_box',
    eligibility_box: 'eligibility_box',
    source_buttons: 'cta_button',
    ribbon: 'ribbon',
  };
  
  const detailsBlockMap: Record<string, string> = {
    terms: 'terms',
    fine_print: 'fine_print',
    source: 'source',
    evidence: 'evidence',
    actions: 'actions',
  };
  
  for (const [sectionKey, enabled] of Object.entries(allSections)) {
    if (typeof enabled !== 'boolean') continue;
    
    const primaryBlockId = primaryBlockMap[sectionKey];
    if (primaryBlockId) {
      draftOverrides.surfaceOverrides!.primary!.blockToggles![primaryBlockId] = enabled;
    }
    
    const detailsBlockId = detailsBlockMap[sectionKey];
    if (detailsBlockId) {
      draftOverrides.surfaceOverrides!.details!.blockToggles![detailsBlockId] = enabled;
    }
  }
  
  return draftOverrides;
}

